import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import SoundService from '../../utils/sounds';
import Spinner from '../ui/Spinner';

const GameNightSchedulerModal = ({ onClose, squads, currentSquad }) => {
  const [mode, setMode] = useState('view'); // 'view' or 'create'
  const [gameNights, setGameNights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [title, setTitle] = useState('Game Night');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('19:00');
  const [duration, setDuration] = useState(2);
  const [squadId, setSquadId] = useState(currentSquad?.id || null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadGameNights();
  }, []);

  const loadGameNights = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/game-nights');
      setGameNights(data);
    } catch (err) { console.error('Failed to load game nights:', err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!scheduledDate || !scheduledTime) return;
    setCreating(true);
    try {
      const scheduled_for = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      await api.post('/api/game-nights', {
        title,
        scheduled_for,
        duration_hours: duration,
        squad_id: squadId,
        notes: notes || null
      });
      SoundService.success();
      setMode('view');
      loadGameNights();
      setTitle('Game Night');
      setScheduledDate('');
      setScheduledTime('19:00');
      setNotes('');
    } catch (err) { alert('Failed to schedule: ' + err.message); }
    finally { setCreating(false); }
  };

  const handleRSVP = async (gameNightId, status) => {
    try {
      await api.post(`/api/game-nights/${gameNightId}/rsvp`, { status });
      SoundService.click();
      loadGameNights();
    } catch (err) { console.error('RSVP failed:', err); }
  };

  const handleCancel = async (gameNightId) => {
    if (!window.confirm('Cancel this game night?')) return;
    try {
      await api.delete(`/api/game-nights/${gameNightId}`);
      SoundService.success();
      loadGameNights();
    } catch (err) { alert('Failed to cancel: ' + err.message); }
  };

  const formatDate = (isoString) => {
    const d = new Date(isoString);
    const now = new Date();
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
    const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    if (d.toDateString() === now.toDateString()) return `Today at ${time}`;
    if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow at ${time}`;
    return `${dayName}, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at ${time}`;
  };

  const getMinDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', padding: '28px', width: '95%', maxWidth: '520px', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '22px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '28px' }}>📅</span> Game Night Scheduler
          </h2>
          <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</motion.button>
        </div>

        {/* Mode Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: THEME.bg, padding: '4px', borderRadius: '10px' }}>
          <button onClick={() => setMode('view')} style={{ flex: 1, background: mode === 'view' ? THEME.accent : 'transparent', color: mode === 'view' ? '#000' : THEME.textMuted, border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '600', cursor: 'pointer' }}>Upcoming</button>
          <button onClick={() => setMode('create')} style={{ flex: 1, background: mode === 'create' ? THEME.accent : 'transparent', color: mode === 'create' ? '#000' : THEME.textMuted, border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '600', cursor: 'pointer' }}>Schedule New</button>
        </div>

        {mode === 'view' ? (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={32} /></div>
            ) : gameNights.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: THEME.textMuted }}>
                <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}>📅</div>
                <p style={{ margin: 0 }}>No upcoming game nights</p>
                <button onClick={() => setMode('create')} style={{ marginTop: '16px', background: THEME.accent, color: '#000', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '600', cursor: 'pointer' }}>Schedule One</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {gameNights.map(gn => (
                  <div key={gn.id} style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', border: `1px solid ${THEME.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', color: THEME.text, fontSize: '16px', fontWeight: '600' }}>{gn.title}</h3>
                        <div style={{ color: THEME.accent, fontSize: '14px', fontWeight: '500' }}>{formatDate(gn.scheduled_for)}</div>
                        {gn.squad && <div style={{ color: THEME.textMuted, fontSize: '12px', marginTop: '4px' }}>{gn.squad.icon} {gn.squad.name}</div>}
                      </div>
                      {gn.is_creator && (
                        <button onClick={() => handleCancel(gn.id)} style={{ background: 'transparent', border: 'none', color: THEME.textMuted, fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                      )}
                    </div>

                    {gn.game && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '8px', background: THEME.bgCard, borderRadius: '8px' }}>
                        <img src={gn.game.header_image} alt="" style={{ width: '60px', height: '28px', objectFit: 'cover', borderRadius: '4px' }} />
                        <span style={{ color: THEME.text, fontSize: '13px', fontWeight: '500' }}>{gn.game.name}</span>
                      </div>
                    )}

                    {/* RSVPs */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {gn.rsvps.map(r => (
                        <div key={r.user_id} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: r.status === 'yes' ? `${THEME.success}20` : r.status === 'no' ? `${THEME.danger}20` : THEME.bgCard, borderRadius: '6px', fontSize: '12px' }}>
                          <span style={{ color: r.color }}>{r.display_name}</span>
                          <span>{r.status === 'yes' ? '✓' : r.status === 'no' ? '✕' : '?'}</span>
                        </div>
                      ))}
                    </div>

                    {/* RSVP Buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['yes', 'maybe', 'no'].map(status => (
                        <button
                          key={status}
                          onClick={() => handleRSVP(gn.id, status)}
                          style={{
                            flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                            background: gn.my_rsvp === status ? (status === 'yes' ? THEME.success : status === 'no' ? THEME.danger : THEME.warning) : THEME.bgCard,
                            color: gn.my_rsvp === status ? '#000' : THEME.textMuted
                          }}
                        >
                          {status === 'yes' ? '✓ Going' : status === 'no' ? '✕ Can\'t' : '? Maybe'}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase' }}>Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Game Night" style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px', color: THEME.text, fontSize: '14px', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase' }}>Date</label>
                <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} min={getMinDate()} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px', color: THEME.text, fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase' }}>Time</label>
                <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px', color: THEME.text, fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase' }}>Duration (hours)</label>
              <select value={duration} onChange={e => setDuration(Number(e.target.value))} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px', color: THEME.text, fontSize: '14px' }}>
                {[1, 1.5, 2, 2.5, 3, 4, 5, 6].map(h => <option key={h} value={h}>{h} hour{h !== 1 ? 's' : ''}</option>)}
              </select>
            </div>

            {squads && squads.length > 0 && (
              <div>
                <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase' }}>Squad (optional)</label>
                <select value={squadId || ''} onChange={e => setSquadId(e.target.value ? Number(e.target.value) : null)} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px', color: THEME.text, fontSize: '14px' }}>
                  <option value="">Personal</option>
                  {squads.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase' }}>Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any details..." rows={3} style={{ width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px', color: THEME.text, fontSize: '14px', resize: 'none', boxSizing: 'border-box' }} />
            </div>

            <button onClick={handleCreate} disabled={creating || !scheduledDate} style={{ background: THEME.accent, color: '#000', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '700', fontSize: '15px', cursor: creating || !scheduledDate ? 'not-allowed' : 'pointer', opacity: !scheduledDate ? 0.5 : 1 }}>
              {creating ? 'Scheduling...' : '📅 Schedule Game Night'}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default GameNightSchedulerModal;
